import {
	signIn,
	SignInModel,
	SignInResult
} from "client/Api/InstanceOperatorPage/instanceOperatorPage";
import {
	BusyIndicator,
	Form,
	FormItem,
	KeyEventArguments,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import { isEmailAddress, isNotEmpty } from "client/Validation/rules";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { navigateTo } from "client_legacy/ndaw/navigator";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { createRoot } from "react-dom/client";
import { ContentBlock } from "../PublicPage/ContentBlock";
import { ensureNotNil } from "client/Misc/utils";

const SignIn = (): ReactElement => {

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [isCapslockOn, setIsCapslockOn] = useState<boolean>(false);
	const [formData, setFormData] = useState<SignInModel>({
		emailAddress: "",
		password: ""
	});

	const formHandler: IFormHandler<SignInModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const { validation } = formHandler;

			try {
				const validationResult: InputValidationResult = await validation.validate(globalCancellation);
				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				const result: SignInResult = await signIn(formData, globalCancellation);

				if (result.wasSuccessful) {
					navigateTo(result.redirectUrl);
				} else {
					alertManager.showError(localize("_WrongCredentials"));
					setIsBusy(false);
				}
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_WrongCredentials"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formData,
			formHandler,
			globalCancellation,
			isBusy
		]
	);

	const passwordKeyDownHandler = useCallback(
		(args: KeyEventArguments): boolean => {
			setIsCapslockOn(args.modifiers.capsLock);
			return false;
		},
		[]
	);

	return (
		<ContentBlock size="small">
			{isBusy && (
				<BusyIndicator />
			)}

			<h1>Admin</h1>

			<Form onSubmit={submitHandler} trackingId="instance-operator-sign-in">
				<FormItem>
					<TextInput
						{...formHandler.for("emailAddress").attachToTextInput()}
						type="email"
						placeholder={localize("_EmailAddress")}
						autoFocus="focus"
					/>

					<ValidationError {...formHandler.for("emailAddress").attachToValidationError()} />
				</FormItem>

				<FormItem>
					<TextInput
						{...formHandler.for("password")
							.attachToTextInput()
							.withOnBlur(() => setIsCapslockOn(false))
						}
						type="password"
						placeholder={localize("_Password")}
						onKeyDown={passwordKeyDownHandler}
					/>

					{isCapslockOn && (
						<ValidationError
							message={localize("_CapsLockIsPressed")}
						/>
					)}

					<ValidationError {...formHandler.for("password").attachToValidationError()} />
				</FormItem>

				<Toolbar horizontalAlignment="right">
					<Toolbar.Button
						kind="primary"
						onClick={submitHandler}
						text={localize("_SignIn")}
						trackingId="sign-in"
						responsiveBehavior="always-visible"
					/>

				</Toolbar>
			</Form>
		</ContentBlock>
	);
};
SignIn.displayName = "SignIn";

const validationConfiguration: IValidationConfiguration<SignInModel> = createValidationConfiguration<SignInModel>();

validationConfiguration.forScalar("emailAddress")
	.add(
		isNotEmpty(localize("_ValidationRequired")),
		isEmailAddress(localize("_ValidationEmail"))
	);

validationConfiguration.forScalar("password")
	.add(
		isNotEmpty(localize("_ValidationRequired"))
	);

export function init(): void {
	createRoot(
		ensureNotNil(
			document.getElementById("instance-operator-sign-in")
		)
	)
		.render(
			<SignIn />
		);
}
